import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getJob,
  toggleSavedJob,
  checkJobSaved,
  applyJob,
  checkJobApplied,
} from "../../api/jobs";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { getFullImageUrl } from "../../utils/imageHandler";
import Loader from "../../components/ui/Loader";
import AnimatedBackground from "../../components/ui/AnimatedBackground";
import {
  Building,
  MapPin,
  Calendar,
  Clock,
  Briefcase,
  DollarSign,
  Users,
  Star,
  CheckCircle,
  ArrowLeft,
  Share2,
  Bookmark,
  Search,
  ArrowRight,
  ChevronRight,
  Zap,
  FileText,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";

const ShareModal = React.lazy(() => import("../../components/common/ShareModal"));

export default function JobDetails() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    if (user) {
      checkIfSaved();
      checkProfileStatus();
      checkIfApplied();
    } else {
      setIsSaved(false);
      setHasApplied(false);
    }
  }, [jobId, user]);

  const fetchJobDetails = async () => {
    if (!jobId || jobId === "undefined") {
      toast.error("Invalid Job URL");
      navigate("/jobs");
      return;
    }
    try {
      const data = await getJob(jobId);
      setJob(data.data);
    } catch (error) {
      console.error("fetchJobDetails error:", error);
      if (error.response?.status === 404) {
        toast.error("Job not found");
      } else {
        toast.error("Failed to load job details");
      }
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const status = await checkJobSaved(jobId);
      setIsSaved(status);
    } catch (error) {
      console.error("Error checking saved status", error);
    }
  };

  const checkIfApplied = async () => {
    try {
      const applied = await checkJobApplied(jobId);
      setHasApplied(applied);
    } catch (error) {
      console.error("Error checking applied status", error);
    }
  };

  const checkProfileStatus = async () => {
    try {
      const res = await api.get("/applier/profile");
      const profile = res.data;

      // Consider profile complete when core fields and a resume exist
      const isComplete =
        !!profile &&
        !!profile.fullName &&
        !!profile.education &&
        !!profile.phone &&
        !!profile.university &&
        profile.gpa != null &&
        !!profile.gender &&
        !!profile.resumeUrl;

      setProfileComplete(isComplete);
    } catch (error) {
      console.error("Error checking profile status", error);
      setProfileComplete(false);
    }
  };

  const handleToggleSave = async () => {
    if (!user) return toast.error("Please login to save jobs");
    setSavingJob(true);
    try {
      const res = await toggleSavedJob(jobId);
      setIsSaved(res.isSaved);
      toast.success(res.isSaved ? "Job saved!" : "Job removed from saved");
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setSavingJob(false);
    }
  };

  const handleApplyClick = () => {
    if (!user) return toast.error("Please login to apply");
    if (hasApplied) {
      return toast.error("You have already applied for this job.");
    }
    setShowApplicationDialog(true);
  };

  const handleQuickApply = async () => {
    setApplying(true);
    try {
      await applyJob(jobId);
      toast.success("Application submitted successfully!");
      setHasApplied(true);
      setShowApplicationDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Application failed");
    } finally {
      setApplying(false);
    }
  };

  const handleGoToProfile = () => {
    setShowApplicationDialog(false);
    navigate("/applier/profile-form");
  };

  if (loading) return <Loader />;

  if (job?.status === 'EXPIRED' || job?.status === 'CLOSED') {
    return <ExpiredJobView job={job} navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-transparent relative">
      <AnimatedBackground />
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[40%] h-[40%] bg-primary/5 blur-[120px] -z-10" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-indigo-500/5 blur-[120px] -z-10" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-8 group text-foreground/60 hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-start">
          {/* Main Info */}
          <div className="lg:col-span-8 space-y-8">
            <Card className="border-0 shadow-2xl rounded-[2.5rem] bg-card/60 backdrop-blur-xl overflow-hidden">
              <div className="p-8 sm:p-12 space-y-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6">
                  <div className="flex flex-col items-center sm:items-start gap-4">
                    <Avatar className="w-20 h-20 rounded-2xl border-4 border-background shadow-xl overflow-hidden bg-white">
                      { (job.companyLogo || job.recruiter?.logoUrl) && (
                        <img 
                          src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
                          alt="Company Logo"
                          className="w-full h-full object-contain"
                        />
                      )}
                      <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-black text-2xl uppercase">
                        {job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-center sm:text-left space-y-1">
                      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{job.title}</h1>
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                        <span className="text-indigo-600 font-bold">{job.companyName || job.recruiter?.companyName}</span>
                        <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                        <span className="text-muted-foreground font-medium flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.location || "Remote"}</span>
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-indigo-500/10 text-indigo-600 border-indigo-200 px-4 py-1.5 rounded-xl font-bold uppercase tracking-widest text-[10px]">
                    {job.workType || "Full-Time"}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                  <div className="p-4 rounded-2xl bg-muted/30 border border-border/50 text-center">
                    <DollarSign className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-[10px] font-black uppercase text-muted-foreground">Salary</p>
                    <p className="text-sm font-bold truncate">{job.salaryRange || "Competitive"}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-muted/30 border border-border/50 text-center">
                    <Briefcase className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-[10px] font-black uppercase text-muted-foreground">Experience</p>
                    <p className="text-sm font-bold truncate">{job.experienceLevel || "Mixed"}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-muted/30 border border-border/50 text-center">
                    <Users className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-[10px] font-black uppercase text-muted-foreground">Openings</p>
                    <p className="text-sm font-bold">{job.openingsCount || 1}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-muted/30 border border-border/50 text-center">
                    <Calendar className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-[10px] font-black uppercase text-muted-foreground">Posted</p>
                    <p className="text-sm font-bold">{format(new Date(job.createdAt), "MMM d")}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-black mb-4">Description</h3>
                    <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 dark:text-muted-foreground font-medium leading-relaxed">
                      {job.description}
                    </div>
                  </div>
                  
                  {job.requirements && (
                    <div>
                      <h3 className="text-xl font-black mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-orange-500" /> Requirements</h3>
                      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 dark:text-muted-foreground font-medium leading-relaxed">
                        {job.requirements}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="border-0 shadow-xl rounded-3xl bg-indigo-600 text-white p-8">
               <h3 className="text-2xl font-black mb-4 leading-tight">Ready for the next step?</h3>
               <p className="text-indigo-100 font-medium mb-8 text-sm opacity-90">Apply now to showcase your skills to {job.companyName || "the recruiter"}.</p>
               <div className="space-y-3">
                 <Button 
                   onClick={handleApplyClick} 
                   disabled={
                     job.status === 'EXPIRED' ||
                     job.status === 'CLOSED' ||
                     ['ADMIN', 'RECRUITER'].includes(user?.role) ||
                     hasApplied
                   }
                   className="w-full h-14 rounded-2xl bg-white text-indigo-600 hover:bg-indigo-50 font-black text-lg transition-all scale-100 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-white/10"
                 >
                    {job.status === 'EXPIRED' || job.status === 'CLOSED'
                      ? "Applications Closed"
                      : ['ADMIN', 'RECRUITER'].includes(user?.role)
                        ? "Appliers Only"
                        : hasApplied
                          ? "Already Applied"
                          : "Apply Now"}
                 </Button>
                 <Button 
                    onClick={handleToggleSave} 
                    className={`w-full h-14 rounded-2xl font-black transition-all hover:scale-[1.02] shadow-lg ${
                      isSaved 
                      ? 'bg-indigo-700 text-white border-indigo-500' 
                      : 'bg-white/10 hover:bg-white/20 text-white border-white/40'
                    }`}
                    variant="outline"
                  >
                    {isSaved ? <Star className="w-5 h-5 mr-2 fill-current text-yellow-400" /> : <Bookmark className="w-5 h-5 mr-2" />}
                    <span className="relative z-10">{isSaved ? "Saved" : "Save Job"}</span>
                  </Button>
               </div>
            </Card>

            <Card className="border border-border rounded-3xl bg-card/40 backdrop-blur-md p-6">
               <div className="flex flex-col gap-4">
                  <p className="text-sm font-black uppercase tracking-widest text-muted-foreground/60 text-center">Share with friends</p>
                  <Button onClick={() => setShareDialogOpen(true)} variant="secondary" className="h-12 rounded-xl font-bold bg-primary/5 text-primary hover:bg-primary hover:text-white group">
                    <Share2 className="w-4 h-4 mr-2" /> Share this opportunity
                  </Button>
               </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Reusable Share Modal integrated */}
      <ShareModal 
        open={shareDialogOpen} 
        onOpenChange={setShareDialogOpen} 
        title={`New Opportunity: ${job.title} at ${job.companyName || "startup"}`}
        url={window.location.href}
        description={`Help a friend find their next dream job.`}
      />

      {/* Application Dialog */}
      <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
         <DialogContent className="rounded-[2.5rem] bg-card/95 backdrop-blur-3xl border-border max-w-md w-[95vw] p-0 overflow-hidden outline-none">
            <DialogHeader className="p-8 text-center bg-muted/20">
               <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                 <Zap className="w-8 h-8 text-emerald-500" />
               </div>
               <DialogTitle className="text-2xl font-black">How would you like to apply?</DialogTitle>
               <DialogDescription className="font-medium">
                 You're applying for <span className="text-foreground font-semibold">{job.title}</span>
               </DialogDescription>
            </DialogHeader>
            <div className="p-8 space-y-6">
               <div className="bg-muted/30 border border-border rounded-2xl p-6 space-y-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    Use your current profile to send a quick application.
                  </p>
                  {!profileComplete && (
                    <p className="text-xs font-semibold text-amber-600">
                      We noticed your profile may be incomplete. You can still apply now or update your profile first.
                    </p>
                  )}
                  <Button
                    onClick={handleQuickApply}
                    disabled={applying}
                    className="w-full h-12 rounded-xl font-black bg-emerald-600 hover:bg-emerald-700 shadow-xl shadow-emerald-500/20 text-white"
                  >
                     {applying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Apply now with my profile"}
                  </Button>
               </div>
               <div className="bg-muted/10 border border-dashed border-border rounded-2xl p-6 space-y-3">
                 <p className="text-sm font-medium text-muted-foreground">
                   Prefer to polish your profile first?
                 </p>
                 <Button
                   onClick={handleGoToProfile}
                   variant="outline"
                   className="w-full h-12 rounded-xl font-black"
                 >
                   Update my profile & apply later
                 </Button>
               </div>
               <p className="text-[10px] text-center text-muted-foreground italic tracking-wide">
                 By applying, your profile details will be shared with the recruiter.
               </p>
            </div>
         </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpiredJobView({ job, navigate }) {
  return (
    <div className="min-h-screen bg-transparent relative flex flex-col items-center justify-center p-4">
      <AnimatedBackground />
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[40%] h-[40%] bg-amber-500/5 blur-[120px] -z-10" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-orange-500/5 blur-[120px] -z-10" />

      <Card className="max-w-2xl w-full border-0 shadow-2xl rounded-[3rem] bg-card/60 backdrop-blur-xl overflow-hidden animate-in fade-in zoom-in duration-700">
        <div className="relative p-8 sm:p-12 text-center">
          {/* Status Icon */}
          <div className="mb-8 relative inline-flex">
            <div className="w-24 h-24 rounded-[2rem] bg-amber-500/10 flex items-center justify-center relative z-10">
              <Clock className="w-12 h-12 text-amber-500 animate-pulse" />
            </div>
            <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full scale-150 -z-10 animate-pulse" />
          </div>

          {/* Text Content */}
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4 text-foreground">
            {job.status === 'CLOSED' ? 'Opportunity Closed' : 'Posting Expired'}
          </h1>
          <p className="text-xl text-muted-foreground font-medium mb-12 max-w-lg mx-auto leading-relaxed">
            The position for <span className="text-foreground font-bold">{job.title}</span> at <span className="text-foreground font-bold">{job.companyName || "the company"}</span> is no longer accepting applications.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Button 
              onClick={() => navigate("/")} 
              className="h-14 rounded-2xl bg-primary text-white font-black text-lg transition-all hover:scale-[1.02] shadow-xl shadow-primary/20"
            >
              <Search className="w-5 h-5 mr-2" /> Browse Jobs
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate(-1)} 
              className="h-14 rounded-2xl border-border hover:bg-muted font-black text-lg transition-all hover:scale-[1.02]"
            >
              Go Back
            </Button>
          </div>

          <div className="mt-12 pt-8 border-t border-border/50">
            <p className="text-sm font-bold text-muted-foreground/60 uppercase tracking-widest mb-4">You might also like</p>
            <div className="flex flex-col gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/jobs")} 
                className="w-full h-12 justify-between rounded-xl hover:bg-primary/5 text-primary font-bold group"
              >
                Find similar roles in {job.category || "this field"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
